import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import AppLayout from '../components/patient-portal/AppLayout';
import Sidebar, { SidebarMenuGroup, SidebarMenuItem } from '../components/patient-portal/Sidebar';
import Topbar from '../components/patient-portal/Topbar';
import AppointmentsPage from './patient-portal/AppointmentsPage';
import BillingPage from './patient-portal/BillingPage';
import CarePlansPage from './patient-portal/CarePlansPage';
import ContactsPage from './patient-portal/ContactsPage';
import FormsPage from './patient-portal/FormsPage';
import HistoryPage from './patient-portal/HistoryPage';
import HomePage from './patient-portal/HomePage';
import LabResultsPage from './patient-portal/LabResultsPage';
import MedicalRecordsPage from './patient-portal/MedicalRecordsPage';
import MessagesPage from './patient-portal/MessagesPage';
import PrescriptionsPage from './patient-portal/PrescriptionsPage';
import ReportsPage from './patient-portal/ReportsPage';
import SettingsPage from './patient-portal/SettingsPage';
import SupportPage from './patient-portal/SupportPage';
import WebmdPage from './patient-portal/WebmdPage';
import { ErrorBanner, formatPortalDateTime } from './patient-portal/ui';
import { PortalPatientDataProvider, usePortalPatientData } from './patient-portal/portalContext';
import { FEATURES } from '../config/features';
import './patientPortal.css';

// Nav item ids gated behind feature flags (backend endpoints not yet built).
// When a flag is false the nav entry and its route are hidden.
const FEATURE_GATED_ITEMS: Record<string, boolean> = {
  labs: FEATURES.labReports,
  prescriptions: FEATURES.prescriptions,
  'care-plans': FEATURES.carePlans,
  webmd: FEATURES.webmd,
};

const isItemEnabled = (id: string) => FEATURE_GATED_ITEMS[id] !== false;

// `title` / `label` here are English fallbacks; the shell resolves the real
// display strings via i18n (nav.groups.<groupKey> / nav.items.<id>) at render.
type NavGroupDef = SidebarMenuGroup & { groupKey: string };

const NAV_GROUPS: NavGroupDef[] = [
  {
    groupKey: 'dashboard',
    title: 'Dashboard',
    items: [
      { id: 'home', label: 'Home', path: '/', icon: 'ph-house' },
      { id: 'appointments', label: 'Appointments', path: '/appointments', icon: 'ph-calendar-check' },
      { id: 'messages', label: 'Messages', path: '/messages', icon: 'ph-chat-circle-text' },
      { id: 'contacts', label: 'Contacts', path: '/contacts', icon: 'ph-users-three' },
    ],
  },
  {
    groupKey: 'medical',
    title: 'Medical',
    items: [
      { id: 'labs', label: 'Lab Results', path: '/lab-results', icon: 'ph-flask' },
      { id: 'records', label: 'Medical Records', path: '/medical-records', icon: 'ph-files' },
      { id: 'prescriptions', label: 'Prescriptions', path: '/prescriptions', icon: 'ph-pill' },
      { id: 'care-plans', label: 'Care Plans', path: '/care-plans', icon: 'ph-heartbeat' },
      { id: 'forms', label: 'Forms', path: '/forms', icon: 'ph-clipboard-text' },
    ],
  },
  {
    groupKey: 'finance',
    title: 'Finance',
    items: [
      { id: 'billing', label: 'Billing', path: '/billing', icon: 'ph-receipt' },
      { id: 'history', label: 'History', path: '/history', icon: 'ph-clock-counter-clockwise' },
      { id: 'reports', label: 'Reports', path: '/reports', icon: 'ph-chart-line-up' },
    ],
  },
  {
    groupKey: 'understand',
    title: 'Understand',
    items: [
      { id: 'webmd', label: 'WebMD', path: '/webmd', icon: 'ph-book-open' },
      { id: 'support', label: 'Support', path: '/support', icon: 'ph-lifebuoy' },
    ],
  },
];

const FOOTER_ITEMS: SidebarMenuItem[] = [
  { id: 'settings', label: 'Settings', path: '/settings', icon: 'ph-gear-six' },
  { id: 'logout', label: 'Log Out', action: 'logout', icon: 'ph-sign-out' },
];

// Drop feature-flagged-off items, then drop any group left with no items.
const VISIBLE_NAV_GROUPS: NavGroupDef[] = NAV_GROUPS.map((group) => ({
  ...group,
  items: group.items.filter((item) => isItemEnabled(item.id)),
})).filter((group) => group.items.length > 0);

const ROUTABLE_ITEMS = [...VISIBLE_NAV_GROUPS.flatMap((group) => group.items), ...FOOTER_ITEMS.filter((item) => item.path)];

function initials(firstName?: string | null, lastName?: string | null) {
  const first = String(firstName || '').trim().charAt(0).toUpperCase();
  const last = String(lastName || '').trim().charAt(0).toUpperCase();
  return `${first || 'P'}${last || ''}`;
}

function PatientPortalShell() {
  const { logout } = useAuth0();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    contexts,
    activeContext,
    profile,
    loading,
    profileLoading,
    error,
    setActivePatientId,
    refreshPortalData,
  } = usePortalPatientData();
  const [topSearch, setTopSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(() => new Date().toISOString());

  // Resolve nav display strings via i18n while keeping ids/paths/icons intact.
  const localizedNavGroups = useMemo<SidebarMenuGroup[]>(
    () =>
      VISIBLE_NAV_GROUPS.map((group) => ({
        title: t(`nav.groups.${group.groupKey}`),
        items: group.items.map((item) => ({ ...item, label: t(`nav.items.${item.id}`) })),
      })),
    [t],
  );
  const localizedFooterItems = useMemo<SidebarMenuItem[]>(
    () => FOOTER_ITEMS.map((item) => ({ ...item, label: t(`nav.items.${item.id}`) })),
    [t],
  );

  const patientDisplayName = profile
    ? `${profile.firstName} ${profile.lastName}`.trim()
    : activeContext
      ? `${activeContext.firstName} ${activeContext.lastName}`.trim()
      : t('common.patient');

  const activeMenuId = useMemo(() => {
    const normalizedPath = location.pathname === '/home' ? '/' : location.pathname;
    const matched = ROUTABLE_ITEMS.find((item) => item.path === normalizedPath);
    return matched?.id || 'home';
  }, [location.pathname]);

  const onMenuClick = (item: SidebarMenuItem) => {
    if (item.action === 'logout') {
      logout({ logoutParams: { returnTo: window.location.origin } });
      return;
    }
    if (item.path) {
      navigate(item.path);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshPortalData();
      setLastRefreshedAt(new Date().toISOString());
    } finally {
      setRefreshing(false);
    }
  };

  if (loading || profileLoading) {
    return <div className="p-6 text-sm text-slate-500">{t('layout.loadingPortal')}</div>;
  }

  return (
    <AppLayout
      sidebar={
        <Sidebar navGroups={localizedNavGroups} footerItems={localizedFooterItems} activeMenuId={activeMenuId} onMenuClick={onMenuClick} />
      }
      topbar={
        <Topbar
          topSearch={topSearch}
          onTopSearchChange={setTopSearch}
          activeContext={activeContext}
          contexts={contexts}
          onContextChange={setActivePatientId}
          refreshing={refreshing}
          onRefresh={onRefresh}
          patientDisplayName={patientDisplayName}
          avatarInitials={initials(profile?.firstName, profile?.lastName)}
        />
      }
    >
      <div className="px-6 pt-4">
        <div className="space-y-3">
          {error ? <ErrorBanner message={error} /> : null}
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500 shadow-soft">
            {t('layout.activePatient')}: {patientDisplayName}
            {activeContext?.mrn ? ` · ${t('layout.mrn', { mrn: activeContext.mrn })}` : ''}
            {activeContext?.relationship ? ` · ${activeContext.relationship === 'SELF' ? t('common.selfAccess') : activeContext.relationship}` : ''}
            {lastRefreshedAt ? ` · ${t('layout.lastRefresh', { value: formatPortalDateTime(lastRefreshedAt) })}` : ''}
            {topSearch ? ` · ${t('layout.search', { query: topSearch })}` : ''}
          </div>
        </div>
      </div>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/home" element={<Navigate to="/" replace />} />
        <Route path="/appointments" element={<AppointmentsPage />} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/contacts" element={<ContactsPage />} />
        {FEATURES.labReports ? <Route path="/lab-results" element={<LabResultsPage />} /> : null}
        <Route path="/medical-records" element={<MedicalRecordsPage />} />
        {FEATURES.prescriptions ? <Route path="/prescriptions" element={<PrescriptionsPage />} /> : null}
        {FEATURES.carePlans ? <Route path="/care-plans" element={<CarePlansPage />} /> : null}
        <Route path="/forms" element={<FormsPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        {FEATURES.webmd ? <Route path="/webmd" element={<WebmdPage />} /> : null}
        <Route path="/support" element={<SupportPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppLayout>
  );
}

export default function PatientPortal() {
  return (
    <PortalPatientDataProvider>
      <PatientPortalShell />
    </PortalPatientDataProvider>
  );
}
